import { useState, useEffect, useRef, useMemo } from "react";
import { Dimension, DimensionCollection, Entity, Query } from "../../api/OasisBackendApi";
import { IconSearch, IconX, IconLoader2, IconTriangle, IconChartBar, IconTools, IconChartLine } from "@tabler/icons-react";
import Fuse, { FuseResult } from 'fuse.js';
import { PCAResult } from "../../utils/pca";
import { AnalysisTab } from "./AnalysisTopBar";
import { calculateNormalComparison, calculatePCAComparison, generateDifferenceDescriptions, ComparisonResult } from "../../utils/comparison";

interface Point2D {
    x: number;
    y: number;
    entity?: Entity;
    query?: Query;
    isSearchResult?: boolean;
    overlapCount?: number;
    overlapIndex?: number;
}

interface AnalysisSidebarProps {
    dimensions: Dimension[];
    dimensionCollection: DimensionCollection | null;
    selectedDimensions: {
        x: Dimension | null;
        y: Dimension | null;
    };
    onDimensionsChange: (dimensions: { x: Dimension | null; y: Dimension | null }) => void;
    showQueries: boolean;
    onShowQueriesChange: (show: boolean) => void;
    showCoverageHull: boolean;
    onShowCoverageHullChange: (show: boolean) => void;
    searchQuery: string;
    onSearchQueryChange: (query: string) => void;
    onSearch: (query: string, distanceMetric?: "cosine" | "euclidean" | "manhattan", searchType?: "oasis" | "semantic", topK?: number, useWeights?: boolean) => void;
    searchLoading: boolean;
    distanceMetric: "cosine" | "euclidean" | "manhattan";
    onDistanceMetricChange: (metric: "cosine" | "euclidean" | "manhattan") => void;
    searchType: "oasis" | "semantic";
    onSearchTypeChange: (type: "oasis" | "semantic") => void;
    topK: number;
    onTopKChange: (k: number) => void;
    useOasisWeights: boolean;
    onUseOasisWeightsChange: (use: boolean) => void;
    searchResultEntities: Entity[];
    entitiesLoading: boolean;
    queriesLoading: boolean;
    dimensionsLoading: boolean;
    selectedOverlapGroup: Point2D[] | null;
    overlapIndex: number;
    onOverlapIndexChange: (index: number) => void;
    onCloseOverlapViewer: () => void;
    uiMapping?: Record<string, string> | null;
    usePCAMode: boolean;
    onUsePCAModeChange: (use: boolean) => void;
    pcaSelectedDimensions: Dimension[];
    onPcaSelectedDimensionsChange: (dimensions: Dimension[]) => void;
    pcaResult: PCAResult | null;
    activeTab: AnalysisTab;
    isComparisonMode: boolean;
    onComparisonModeChange: (enabled: boolean) => void;
    selectedPoints: [any | null, any | null];
    onSelectedPointsChange: (points: [any | null, any | null]) => void;
    hoveredDimensionId: string | null;
    onHoveredDimensionChange: (dimensionId: string | null) => void;
    comparisonResult: ComparisonResult | null;
    weightAdjustedMode: boolean;
    onWeightAdjustedModeChange: (enabled: boolean) => void;
    queryWeights: Record<string, number>;
    useNormalizedEmbeddings: boolean;
    onUseNormalizedEmbeddingsChange: (enabled: boolean) => void;
    pcaBasisSource: "both" | "entities" | "queries";
    onPcaBasisSourceChange: (source: "both" | "entities" | "queries") => void;
    showVectorField: boolean;
    onShowVectorFieldChange: (enabled: boolean) => void;
    vectorFieldMode: "individual" | "average";
    onVectorFieldModeChange: (mode: "individual" | "average") => void;
    vectorFieldLength: "field" | "full";
    onVectorFieldLengthChange: (length: "field" | "full") => void;
    vectorFieldShimmer: boolean;
    onVectorFieldShimmerChange: (enabled: boolean) => void;
}

const AnalysisSidebar = ({
    dimensions,
    dimensionCollection,
    selectedDimensions,
    onDimensionsChange,
    showQueries,
    onShowQueriesChange,
    showCoverageHull,
    onShowCoverageHullChange,
    searchQuery,
    onSearchQueryChange,
    onSearch,
    searchLoading,
    distanceMetric,
    onDistanceMetricChange,
    searchType,
    onSearchTypeChange,
    topK,
    onTopKChange,
    useOasisWeights,
    onUseOasisWeightsChange,
    searchResultEntities,
    entitiesLoading,
    queriesLoading,
    dimensionsLoading,
    selectedOverlapGroup,
    overlapIndex,
    onOverlapIndexChange,
    onCloseOverlapViewer,
    uiMapping,
    usePCAMode,
    onUsePCAModeChange,
    pcaSelectedDimensions,
    onPcaSelectedDimensionsChange,
    pcaResult,
    activeTab,
    isComparisonMode,
    onComparisonModeChange,
    selectedPoints,
    onSelectedPointsChange,
    hoveredDimensionId,
    onHoveredDimensionChange,
    comparisonResult: comparisonResultProp,
    weightAdjustedMode,
    onWeightAdjustedModeChange,
    queryWeights,
    useNormalizedEmbeddings,
    onUseNormalizedEmbeddingsChange,
    pcaBasisSource,
    onPcaBasisSourceChange,
    showVectorField,
    onShowVectorFieldChange,
    vectorFieldMode,
    onVectorFieldModeChange,
    vectorFieldLength,
    onVectorFieldLengthChange,
    vectorFieldShimmer,
    onVectorFieldShimmerChange
}: AnalysisSidebarProps) => {
    const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
    const [xDimensionSearch, setXDimensionSearch] = useState("");
    const [yDimensionSearch, setYDimensionSearch] = useState("");
    const [showXDropdown, setShowXDropdown] = useState(false);
    const [showYDropdown, setShowYDropdown] = useState(false);
    const xInputRef = useRef<HTMLInputElement>(null);
    const yInputRef = useRef<HTMLInputElement>(null);
    const xDropdownRef = useRef<HTMLDivElement>(null);
    const yDropdownRef = useRef<HTMLDivElement>(null);

    // Helper function to extract image URLs from entity content
    const getImageUrls = (entity: Entity): string[] => {
        if (!uiMapping?.image_urls) return [];
        
        try {
            const imageData = entity.content[uiMapping.image_urls];
            if (typeof imageData === 'string') {
                // Try to parse as JSON first
                try {
                    const parsed = JSON.parse(imageData.replace(/'/g, '"'));
                    if (typeof parsed === 'string') {
                        // Parsed result is a single URL string
                        return [parsed];
                    } else if (Array.isArray(parsed)) {
                        // Parsed result is an array
                        return parsed;
                    } else {
                        return [];
                    }
                } catch {
                    // If JSON parsing fails, treat as single URL and convert to array
                    return [imageData];
                }
            } else if (Array.isArray(imageData)) {
                return imageData;
            } else {
                return [];
            }
        } catch (error) {
            console.error("Error parsing image urls", error);
            return [];
        }
    };

    // Helper function to extract title from entity content
    const getTitle = (entity: Entity): string => {
        if (!uiMapping?.title) return 'Untitled';
        return entity.content[uiMapping.title] || 'Untitled';
    };

    // Helper function to extract description from entity content
    const getDescription = (entity: Entity): string => {
        if (!uiMapping?.description) return '';
        return entity.content[uiMapping.description] || '';
    };

    // Helper function to extract URL from entity content
    const getUrl = (entity: Entity): string => {
        if (!uiMapping?.url) return '';
        return entity.content[uiMapping.url] || '';
    };

    // Helper function to handle entity click
    const handleEntityClick = (entity: Entity) => {
        const url = getUrl(entity);
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (localSearchQuery.trim()) {
            onSearchQueryChange(localSearchQuery);
            onSearch(localSearchQuery, distanceMetric, searchType, topK, useOasisWeights);
        }
    };

    const handleDimensionSelect = (dimension: Dimension, axis: 'x' | 'y') => {
        const newDimensions = { ...selectedDimensions };
        
        // If selecting the same dimension for both axes, swap them
        if (axis === 'x' && selectedDimensions.y?.id === dimension.id) {
            newDimensions.y = selectedDimensions.x;
        } else if (axis === 'y' && selectedDimensions.x?.id === dimension.id) {
            newDimensions.x = selectedDimensions.y;
        }
        
        newDimensions[axis] = dimension;
        onDimensionsChange(newDimensions);
    };

    const clearDimension = (axis: 'x' | 'y') => {
        const newDimensions = { ...selectedDimensions };
        newDimensions[axis] = null;
        onDimensionsChange(newDimensions);
    };

    const getLiveDimensions = () => {
        if (!dimensionCollection) return [];
        return dimensionCollection.dimensions;
    };

    const liveDimensions = getLiveDimensions();
    const availableDimensions = liveDimensions.length > 0 ? liveDimensions : dimensions;
    
    // Calculate top contributing dimensions from PCA result
    const topContributingDimensions = useMemo(() => {
        if (!pcaResult || !pcaResult.dimensionVectors) return [];
        
        // Calculate total magnitude for normalization
        const totalMagnitude = pcaResult.dimensionVectors.reduce((sum, v) => sum + v.magnitude, 0);
        
        // Sort by magnitude (contribution) descending and add normalized percentage
        return [...pcaResult.dimensionVectors]
            .sort((a, b) => b.magnitude - a.magnitude)
            .map((vector, idx) => ({
                ...vector,
                rank: idx + 1,
                contributionPercent: totalMagnitude > 0 ? (vector.magnitude / totalMagnitude) * 100 : 0
            } as typeof vector & { rank: number; contributionPercent: number }));
    }, [pcaResult]);

    // Fuzzy search function for dimensions
    const getFilteredDimensions = (searchQuery: string): Dimension[] => {
        if (!searchQuery.trim()) {
            // When search is empty, return all dimensions
            return availableDimensions;
        }
        
        const fuse = new Fuse(availableDimensions, {
            keys: ['name', 'description'],
            threshold: 0.3,
            ignoreLocation: true,
        });
        
        return fuse.search(searchQuery).map((result: FuseResult<Dimension>) => result.item);
    };

    const filteredXDimensions = getFilteredDimensions(xDimensionSearch);
    const filteredYDimensions = getFilteredDimensions(yDimensionSearch);

    // Handle click outside to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (xInputRef.current && !xInputRef.current.contains(event.target as Node) &&
                xDropdownRef.current && !xDropdownRef.current.contains(event.target as Node)) {
                setShowXDropdown(false);
            }
            if (yInputRef.current && !yInputRef.current.contains(event.target as Node) &&
                yDropdownRef.current && !yDropdownRef.current.contains(event.target as Node)) {
                setShowYDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Update search text when dimension is selected
    useEffect(() => {
        if (selectedDimensions.x) {
            setXDimensionSearch(selectedDimensions.x.name);
        } else {
            setXDimensionSearch("");
        }
    }, [selectedDimensions.x]);

    useEffect(() => {
        if (selectedDimensions.y) {
            setYDimensionSearch(selectedDimensions.y.name);
        } else {
            setYDimensionSearch("");
        }
    }, [selectedDimensions.y]);

    // Use comparison result from props (calculated in parent)
    const comparisonResult = comparisonResultProp;

    const getPointName = (point: any): string => {
        if (point?.entity) {
            if (!uiMapping?.title) return `Entity ${point.entity.id}`;
            return point.entity.content?.[uiMapping.title] || `Entity ${point.entity.id}`;
        } else if (point?.query) {
            return point.query.text || `Query ${point.query.id}`;
        }
        return 'Unknown';
    };

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [sidebarWidth, setSidebarWidth] = useState(288); // w-72 = 288px
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragStartWidth, setDragStartWidth] = useState(0);

    const minWidth = 48; // w-12
    const maxWidth = 800; // Allow much more expansion

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        setDragStartX(e.clientX);
        setDragStartWidth(sidebarWidth);
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging) return;
            
            const deltaX = e.clientX - dragStartX; // Positive when dragging right
            const newWidth = Math.max(minWidth, Math.min(maxWidth, dragStartWidth + deltaX));
            setSidebarWidth(newWidth);
            
            // Auto-collapse if dragged very small
            if (newWidth <= minWidth + 10) {
                setIsCollapsed(true);
            } else {
                setIsCollapsed(false);
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDragging, dragStartX, dragStartWidth, minWidth, maxWidth]);

    const handleToggleClick = () => {
        if (isCollapsed) {
            setSidebarWidth(288);
            setIsCollapsed(false);
        } else {
            setSidebarWidth(minWidth);
            setIsCollapsed(true);
        }
    };

    return (
        <div 
            className="bg-gray-800 border-r border-gray-700 flex flex-col flex-shrink-0 h-full relative transition-all duration-200"
            style={{ width: `${sidebarWidth}px`, minWidth: `${minWidth}px` }}
        >
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto h-full">
            
            {/* Drag Handle - Wider and more visible */}
            <div
                onMouseDown={handleMouseDown}
                className={`fixed top-0 bottom-0 w-2 cursor-col-resize z-[100] hover:bg-blue-500/50 transition-colors ${
                    isDragging ? 'bg-blue-500' : 'bg-transparent'
                }`}
                style={{ 
                    left: `${sidebarWidth - 1}px`,
                    right: 'auto'
                }}
                title="Drag to resize sidebar"
            />
            
            {/* Collapse Toggle Button - Fixed position */}
            <button
                onClick={handleToggleClick}
                className="fixed w-6 h-6 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-full flex items-center justify-center text-gray-300 hover:text-white transition-colors shadow-md z-[100]"
                style={{
                    left: `${sidebarWidth - 12}px`,
                    top: '4rem'
                }}
                title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
                {isCollapsed ? '→' : '←'}
            </button>
            
            {!isCollapsed && (
                <>
                    {/* Loading States */}
            {(entitiesLoading || queriesLoading || dimensionsLoading) && (
                <div className="p-3 border-b border-gray-700">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                        <IconLoader2 className="animate-spin" size={16} />
                        <span>
                            {entitiesLoading && "Loading entities..."}
                            {queriesLoading && "Loading queries..."}
                            {dimensionsLoading && "Loading dimensions..."}
                        </span>
                    </div>
                </div>
            )}

            {/* Search Tab Content */}
            {activeTab === 'search' && (
                <>
                    {/* Search */}
                    <div className="p-3 border-b border-gray-700">
                        <h3 className="text-md font-medium text-white mb-3">Search & Highlight</h3>
                        
                        <form onSubmit={handleSearchSubmit} className="space-y-3">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={localSearchQuery}
                                    onChange={(e) => setLocalSearchQuery(e.target.value)}
                                    placeholder="Search for entities..."
                                    className="w-full px-3 py-2 pl-10 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Search Type
                                </label>
                                <select
                                    value={searchType}
                                    onChange={(e) => onSearchTypeChange(e.target.value as "oasis" | "semantic")}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="oasis">Oasis</option>
                                    <option value="semantic">Semantic</option>
                                </select>
                            </div>
                            
                            {searchType === "oasis" && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Distance Metric
                                        </label>
                                        <select
                                            value={distanceMetric}
                                            onChange={(e) => onDistanceMetricChange(e.target.value as "cosine" | "euclidean" | "manhattan")}
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="cosine">Cosine</option>
                                            <option value="euclidean">Euclidean</option>
                                            <option value="manhattan">Manhattan</option>
                                        </select>
                                    </div>
                                    
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-medium text-gray-300">
                                            Use Query Weights
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => onUseOasisWeightsChange(!useOasisWeights)}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                                useOasisWeights ? 'bg-blue-600' : 'bg-gray-600'
                                            }`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                    useOasisWeights ? 'translate-x-6' : 'translate-x-1'
                                                }`}
                                            />
                                        </button>
                                    </div>
                                </>
                            )}
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Number of Results (Top K)
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={topK}
                                    onChange={(e) => onTopKChange(Math.max(1, Math.min(100, parseInt(e.target.value) || 10)))}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            
                            <button
                                type="submit"
                                disabled={!localSearchQuery.trim() || searchLoading}
                                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                            >
                                {searchLoading ? (
                                    <>
                                        <IconLoader2 className="animate-spin" size={16} />
                                        Searching...
                                    </>
                                ) : (
                                    <>
                                        <IconSearch size={16} />
                                        Search
                                    </>
                                )}
                            </button>
                        </form>
                        
                        {searchQuery && (
                            <div className="mt-3 p-2 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                                <p className="text-sm text-blue-300">
                                    Current search: "{searchQuery}"
                                </p>
                                <div className="flex gap-2 mt-2">
                                    <button
                                        onClick={() => {
                                            setLocalSearchQuery("");
                                            onSearchQueryChange("");
                                            onSearch("");
                                        }}
                                        className="text-xs text-blue-400 hover:text-blue-300 px-2 py-1 rounded bg-blue-800/30 hover:bg-blue-700/30"
                                    >
                                        Clear search
                                    </button>
                                    <button
                                        onClick={() => {
                                            onSearch("");
                                        }}
                                        className="text-xs text-orange-400 hover:text-orange-300 px-2 py-1 rounded bg-orange-800/30 hover:bg-orange-700/30"
                                    >
                                        Clear from chart
                                    </button>
                                </div>
                                
                                {/* Weight-Adjusted Mode Toggle */}
                                {Object.keys(queryWeights).length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-blue-700/50">
                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-medium text-blue-300">
                                                Weight-Adjusted View
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => onWeightAdjustedModeChange(!weightAdjustedMode)}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                                    weightAdjustedMode ? 'bg-purple-600' : 'bg-gray-600'
                                                }`}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                        weightAdjustedMode ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                                />
                                            </button>
                                        </div>
                                        <p className="text-xs text-blue-400/70 mt-1">
                                            Transform space by query dimension weights
                                        </p>
                                        
                                        {/* Show weights for selected dimensions */}
                                        {weightAdjustedMode && selectedDimensions.x && selectedDimensions.y && (() => {
                                            // Detect if weights are 0-1 or 0-100 scale
                                            const maxWeight = Object.keys(queryWeights).length > 0 ? Math.max(...Object.values(queryWeights)) : 100;
                                            const isNormalized = maxWeight <= 1;
                                            const getDisplayWeight = (w: number) => isNormalized ? w * 100 : w;
                                            
                                            const weightX = queryWeights[selectedDimensions.x.name] || 0;
                                            const weightY = queryWeights[selectedDimensions.y.name] || 0;
                                            const displayX = getDisplayWeight(weightX);
                                            const displayY = getDisplayWeight(weightY);
                                            
                                            return (
                                                <div className="mt-2 space-y-1">
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-gray-400">X ({selectedDimensions.x.name}):</span>
                                                        <span className={`font-mono ${displayX >= 50 ? 'text-green-400' : 'text-orange-400'}`}>
                                                            w={displayX.toFixed(0)}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-gray-400">Y ({selectedDimensions.y.name}):</span>
                                                        <span className={`font-mono ${displayY >= 50 ? 'text-green-400' : 'text-orange-400'}`}>
                                                            w={displayY.toFixed(0)}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Coverage Hull Toggle */}
                    <div className="p-3 border-b border-gray-700">
                        <div className="flex items-center justify-between">
                            <label className="text-sm text-gray-300">Show Coverage Hull</label>
                            <button
                                onClick={() => onShowCoverageHullChange(!showCoverageHull)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                    showCoverageHull ? 'bg-blue-600' : 'bg-gray-600'
                                }`}
                            >
                                <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        showCoverageHull ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                />
                            </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                            Display search result coverage area with density heatmap
                        </p>
                        
                        {/* Density Legend */}
                        {showCoverageHull && (
                            <div className="mt-3 p-3 bg-gray-700/50 rounded-lg">
                                <h4 className="text-sm font-medium text-white mb-2">Ranking Heatmap</h4>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded" style={{backgroundColor: 'hsla(220, 80%, 60%, 0.2)'}}></div>
                                        <span className="text-xs text-gray-300">Lower ranked results</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded" style={{backgroundColor: 'hsla(220, 90%, 45%, 0.6)'}}></div>
                                        <span className="text-xs text-gray-300">Medium ranked results</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded" style={{backgroundColor: 'hsla(220, 100%, 30%, 0.9)'}}></div>
                                        <span className="text-xs text-gray-300">Top ranked results</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Search Results List */}
                    {searchResultEntities.length > 0 && (
                        <div className="p-3 border-b border-gray-700">
                            <h3 className="text-md font-medium text-white mb-3">
                                Search Results ({searchResultEntities.length})
                            </h3>
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                {searchResultEntities.map((entity, index) => {
                                    const title = getTitle(entity);
                                    const description = getDescription(entity);
                                    const url = getUrl(entity);
                                    
                                    return (
                                        <div
                                            key={entity.id}
                                            className={`p-3 bg-gray-800/50 border border-gray-700 rounded-lg hover:bg-gray-800 hover:border-gray-600 transition-all duration-200 ${
                                                url ? 'cursor-pointer' : ''
                                            }`}
                                            onClick={() => {
                                                if (url) {
                                                    window.open(url, '_blank', 'noopener,noreferrer');
                                                }
                                            }}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs font-medium text-blue-400 bg-blue-900/30 px-2 py-0.5 rounded">
                                                            #{index + 1}
                                                        </span>
                                                        <h4 className="text-sm font-medium text-white truncate">
                                                            {title}
                                                        </h4>
                                                    </div>
                                                    {description && (
                                                        <p className="text-xs text-gray-400 line-clamp-2 mt-1">
                                                            {description}
                                                        </p>
                                                    )}
                                                    {entity.distance !== undefined && entity.distance !== null && (
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            Distance: {(() => {
                                                                const dist = parseFloat(entity.distance);
                                                                return !isNaN(dist) ? dist.toFixed(4) : entity.distance;
                                                            })()}
                                                        </p>
                                                    )}
                                                </div>
                                                {url && (
                                                    <IconSearch 
                                                        size={14} 
                                                        className="text-gray-400 flex-shrink-0 mt-1" 
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Overlap Viewer */}
                    {selectedOverlapGroup && (
                        <div className="p-3 border-b border-gray-700">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-md font-medium text-white">
                                    {selectedOverlapGroup.length > 1 ? `Overlapping Items (${selectedOverlapGroup.length})` : 'Item Details'}
                                </h3>
                                <button
                                    onClick={onCloseOverlapViewer}
                                    className="text-gray-400 hover:text-white text-xl"
                                >
                                    ×
                                </button>
                            </div>
                            
                            {selectedOverlapGroup.length > 1 && (
                                <div className="flex items-center gap-4 mb-3">
                                    <button
                                        onClick={() => onOverlapIndexChange(Math.max(0, overlapIndex - 1))}
                                        disabled={overlapIndex === 0}
                                        className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
                                    >
                                        ← Previous
                                    </button>
                                    <span className="text-gray-300">
                                        {overlapIndex + 1} of {selectedOverlapGroup.length}
                                    </span>
                                    <button
                                        onClick={() => onOverlapIndexChange(Math.min(selectedOverlapGroup.length - 1, overlapIndex + 1))}
                                        disabled={overlapIndex === selectedOverlapGroup.length - 1}
                                        className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
                                    >
                                        Next →
                                    </button>
                                </div>
                            )}

                            <div className="bg-gray-800 rounded-lg p-4 max-h-90 overflow-y-auto">
                                {selectedOverlapGroup[overlapIndex] && (
                                    <div>
                                        {selectedOverlapGroup[overlapIndex].entity && (
                                            <div 
                                                className={`bg-emerald-900/30 border border-emerald-700/50 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 ${
                                                    getUrl(selectedOverlapGroup[overlapIndex].entity!) ? 'cursor-pointer' : ''
                                                }`}
                                                onClick={() => handleEntityClick(selectedOverlapGroup[overlapIndex].entity!)}
                                            >
                                                {(() => {
                                                    const imageUrls = getImageUrls(selectedOverlapGroup[overlapIndex].entity!);
                                                    if (imageUrls.length > 0) {
                                                        return (
                                                            <div className="relative">
                                                                <img 
                                                                    src={imageUrls[0]} 
                                                                    alt={getTitle(selectedOverlapGroup[overlapIndex].entity!)} 
                                                                    className="w-full h-64 object-contain"
                                                                    onError={(e) => {
                                                                        (e.target as HTMLImageElement).style.display = 'none';
                                                                    }}
                                                                />
                                                                {getUrl(selectedOverlapGroup[overlapIndex].entity!) && (
                                                                    <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                                                                        Click to open
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                                
                                                <div className="p-3">
                                                    <h3 className="text-sm text-white font-medium mb-1">
                                                        {getTitle(selectedOverlapGroup[overlapIndex].entity!)}
                                                    </h3>
                                                    
                                                    {getDescription(selectedOverlapGroup[overlapIndex].entity!) && (
                                                        <p className="text-xs text-emerald-200 line-clamp-2 mb-2">
                                                            {getDescription(selectedOverlapGroup[overlapIndex].entity!)}
                                                        </p>
                                                    )}
                                                    
                                                    <div className="text-xs text-emerald-300">
                                                        {(() => {
                                                            const metadata = selectedOverlapGroup[overlapIndex].entity!.embedding?.metadata;
                                                            const preferredKey = useNormalizedEmbeddings ? 'normalized_entity_dimension_index' : 'entity_dimension_index';
                                                            const idx = metadata?.[preferredKey] ?? metadata?.['entity_dimension_index'];
                                                            return (
                                                                <>
                                                                    <div>X: {(idx?.[selectedDimensions.x?.name || ''] !== undefined
                                                                        ? idx[selectedDimensions.x?.name || '']
                                                                        : idx?.[selectedDimensions.x?.id || ''])?.toFixed(3)}</div>
                                                                    <div>Y: {(idx?.[selectedDimensions.y?.name || ''] !== undefined
                                                                        ? idx[selectedDimensions.y?.name || '']
                                                                        : idx?.[selectedDimensions.y?.id || ''])?.toFixed(3)}</div>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {selectedOverlapGroup[overlapIndex].query && (
                                            <div className="bg-orange-900/30 border border-orange-700/50 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                                                <div className="p-3">
                                                    <h3 className="text-sm text-white font-medium mb-2">
                                                        {selectedOverlapGroup[overlapIndex].query!.text}
                                                    </h3>
                                                    <div className="text-xs text-orange-200">
                                                        {(() => {
                                                            const preferred = useNormalizedEmbeddings
                                                                ? selectedOverlapGroup[overlapIndex].query!.normalized_oasis_embedding
                                                                : selectedOverlapGroup[overlapIndex].query!.oasis_embedding;
                                                            const emb = preferred ?? selectedOverlapGroup[overlapIndex].query!.oasis_embedding;
                                                            return (
                                                                <>
                                                                    <div>X: {(emb?.[selectedDimensions.x?.name || ''] !== undefined
                                                                        ? emb[selectedDimensions.x?.name || '']
                                                                        : emb?.[selectedDimensions.x?.id || ''])?.toFixed(3)}</div>
                                                                    <div>Y: {(emb?.[selectedDimensions.y?.name || ''] !== undefined
                                                                        ? emb[selectedDimensions.y?.name || '']
                                                                        : emb?.[selectedDimensions.y?.id || ''])?.toFixed(3)}</div>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Dimensionality Tab Content */}
            {activeTab === 'dimensionality' && (
                <>
                    {/* Dimension Selection */}
                    <div className="p-3 border-b border-gray-700">
                        <h3 className="text-md font-medium text-white mb-3">Dimension Selection</h3>
                        
                        {dimensionCollection && (
                            <div className="mb-3 p-2 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                                <p className="text-xs text-blue-300">
                                    Using live dimension collection ({liveDimensions.length} dimensions)
                                </p>
                            </div>
                        )}

                        {/* X-Axis Selection */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                X-Axis Dimension
                            </label>
                            <div className="relative" ref={xInputRef}>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={xDimensionSearch}
                                        onChange={(e) => {
                                            setXDimensionSearch(e.target.value);
                                            setShowXDropdown(true);
                                        }}
                                        onFocus={() => setShowXDropdown(true)}
                                        placeholder="Search X-axis dimension..."
                                        disabled={usePCAMode}
                                        className={`w-full px-3 py-2 pl-10 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            usePCAMode ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                    />
                                    <IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                                    {selectedDimensions.x && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                clearDimension('x');
                                                setXDimensionSearch("");
                                                setShowXDropdown(false);
                                            }}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                                        >
                                            <IconX size={16} />
                                        </button>
                                    )}
                                </div>
                                {showXDropdown && filteredXDimensions.length > 0 && (
                                    <div
                                        ref={xDropdownRef}
                                        className="absolute z-50 w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                                    >
                                        {filteredXDimensions.map((dimension) => (
                                            <div
                                                key={dimension.id}
                                                onClick={() => {
                                                    handleDimensionSelect(dimension, 'x');
                                                    setXDimensionSearch(dimension.name);
                                                    setShowXDropdown(false);
                                                }}
                                                className={`px-3 py-2 cursor-pointer hover:bg-gray-600 ${
                                                    selectedDimensions.x?.id === dimension.id ? 'bg-blue-600' : ''
                                                }`}
                                            >
                                                <div className="text-white font-medium break-words whitespace-normal">{dimension.name}</div>
                                                {dimension.description && (
                                                    <div className="text-xs text-gray-400 mt-1 break-words whitespace-normal">{dimension.description}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {showXDropdown && xDimensionSearch.trim() && filteredXDimensions.length === 0 && (
                                    <div
                                        ref={xDropdownRef}
                                        className="absolute z-50 w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg p-3"
                                    >
                                        <div className="text-gray-400 text-sm">No dimensions found</div>
                                    </div>
                                )}
                            </div>
                            {selectedDimensions.x && (
                                <p className="text-xs text-gray-400 mt-1">
                                    {selectedDimensions.x.description}
                                </p>
                            )}
                        </div>

                        {/* Y-Axis Selection */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Y-Axis Dimension
                            </label>
                            <div className="relative" ref={yInputRef}>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={yDimensionSearch}
                                        onChange={(e) => {
                                            setYDimensionSearch(e.target.value);
                                            setShowYDropdown(true);
                                        }}
                                        onFocus={() => setShowYDropdown(true)}
                                        placeholder="Search Y-axis dimension..."
                                        disabled={usePCAMode}
                                        className={`w-full px-3 py-2 pl-10 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            usePCAMode ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                    />
                                    <IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                                    {selectedDimensions.y && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                clearDimension('y');
                                                setYDimensionSearch("");
                                                setShowYDropdown(false);
                                            }}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                                        >
                                            <IconX size={16} />
                                        </button>
                                    )}
                                </div>
                                {showYDropdown && filteredYDimensions.length > 0 && (
                                    <div
                                        ref={yDropdownRef}
                                        className="absolute z-50 w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                                    >
                                        {filteredYDimensions.map((dimension) => (
                                            <div
                                                key={dimension.id}
                                                onClick={() => {
                                                    handleDimensionSelect(dimension, 'y');
                                                    setYDimensionSearch(dimension.name);
                                                    setShowYDropdown(false);
                                                }}
                                                className={`px-3 py-2 cursor-pointer hover:bg-gray-600 ${
                                                    selectedDimensions.y?.id === dimension.id ? 'bg-blue-600' : ''
                                                }`}
                                            >
                                                <div className="text-white font-medium break-words whitespace-normal">{dimension.name}</div>
                                                {dimension.description && (
                                                    <div className="text-xs text-gray-400 mt-1 break-words whitespace-normal">{dimension.description}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {showYDropdown && yDimensionSearch.trim() && filteredYDimensions.length === 0 && (
                                    <div
                                        ref={yDropdownRef}
                                        className="absolute z-50 w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg p-3"
                                    >
                                        <div className="text-gray-400 text-sm">No dimensions found</div>
                                    </div>
                                )}
                            </div>
                            {selectedDimensions.y && (
                                <p className="text-xs text-gray-400 mt-1">
                                    {selectedDimensions.y.description}
                                </p>
                            )}
                        </div>

                        {/* Dimension Status */}
                        {selectedDimensions.x && selectedDimensions.y && (
                            <div className="p-3 bg-green-900/20 border border-green-700/50 rounded-lg">
                                <p className="text-sm text-green-300">
                                    ✓ Ready to visualize with {selectedDimensions.x.name} (X) and {selectedDimensions.y.name} (Y)
                                </p>
                                {usePCAMode && (
                                    <p className="text-xs text-yellow-300 mt-1">
                                        ⚠ PCA mode is disabled when dimensions are selected
                                    </p>
                                )}
                            </div>
                        )}
                        {usePCAMode && !selectedDimensions.x && !selectedDimensions.y && (
                            <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                                <p className="text-sm text-blue-300">
                                    ✓ PCA mode active - dimension selection disabled
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Visualization Options */}
                    <div className="p-3 border-b border-gray-700">
                        <h3 className="text-md font-medium text-white mb-3">Visualization Options</h3>
                        
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <label className="text-sm text-gray-300">Show Queries</label>
                                <button
                                    onClick={() => onShowQueriesChange(!showQueries)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                        showQueries ? 'bg-blue-600' : 'bg-gray-600'
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            showQueries ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                    />
                                </button>
                            </div>
                            <p className="text-xs text-gray-400">
                                Display query points in a different color
                            </p>
                        </div>
                    </div>

                    {/* Normalized Embeddings Toggle */}
                    <div className="p-3 border-b border-gray-700">
                        <div className="flex items-center justify-between">
                            <label className="text-sm text-gray-300">Use Normalized Embeddings</label>
                            <button
                                onClick={() => onUseNormalizedEmbeddingsChange(!useNormalizedEmbeddings)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                    useNormalizedEmbeddings ? 'bg-blue-600' : 'bg-gray-600'
                                }`}
                            >
                                <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        useNormalizedEmbeddings ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                />
                            </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                            {useNormalizedEmbeddings
                                ? 'Showing normalized values (used for search computation)'
                                : 'Showing raw classification values'}
                        </p>
                    </div>

                    {/* PCA Analysis */}
                    <div className="p-3 border-b border-gray-700">
                        <h3 className="text-md font-medium text-white mb-3">PCA Analysis</h3>
                        
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <label className={`text-sm ${(selectedDimensions.x || selectedDimensions.y) ? 'text-gray-500' : 'text-gray-300'}`}>
                                    Use PCA Mode
                                </label>
                                <button
                                    onClick={() => {
                                        if (!selectedDimensions.x && !selectedDimensions.y) {
                                            onUsePCAModeChange(!usePCAMode);
                                        }
                                    }}
                                    disabled={!!(selectedDimensions.x || selectedDimensions.y)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                        usePCAMode ? 'bg-blue-600' : 'bg-gray-600'
                                    } ${(selectedDimensions.x || selectedDimensions.y) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            usePCAMode ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                    />
                                </button>
                            </div>
                            <p className={`text-xs ${(selectedDimensions.x || selectedDimensions.y) ? 'text-gray-500' : 'text-gray-400'}`}>
                                {selectedDimensions.x || selectedDimensions.y 
                                    ? 'Clear selected dimensions to enable PCA mode'
                                    : 'Reduce high-dimensional data to 2D using Principal Component Analysis'}
                            </p>
                            
                            {usePCAMode && (
                                <div className="mt-4 space-y-3">
                                    {/* PCA Basis Source */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400 mb-1.5">PCA Basis</label>
                                        <div className="flex rounded-lg overflow-hidden border border-gray-600">
                                            {(["both", "entities", "queries"] as const).map((source) => (
                                                <button
                                                    key={source}
                                                    onClick={() => onPcaBasisSourceChange(source)}
                                                    className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${
                                                        pcaBasisSource === source
                                                            ? 'bg-blue-600 text-white'
                                                            : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                                                    }`}
                                                >
                                                    {source === "both" ? "Both" : source === "entities" ? "Entities" : "Queries"}
                                                </button>
                                            ))}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Fit PCA on {pcaBasisSource === "both" ? "all points" : pcaBasisSource}; all points are still projected.
                                        </p>
                                    </div>

                                    {/* Top Contributing Dimensions */}
                                    {pcaResult && topContributingDimensions.length > 0 && (
                                        <div className="p-3 bg-gray-700/50 rounded-lg border border-gray-600">
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="block text-sm font-medium text-gray-300">
                                                    Top Contributing Dimensions
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <input
                                                    type="number"
                                                    id="top-n-input"
                                                    min="2"
                                                    max={topContributingDimensions.length}
                                                    defaultValue={Math.min(5, topContributingDimensions.length)}
                                                    placeholder="N"
                                                    className="w-16 px-2 py-1 text-sm bg-gray-600 border border-gray-500 rounded text-white"
                                                />
                                                <button
                                                    onClick={() => {
                                                        const input = document.getElementById('top-n-input') as HTMLInputElement;
                                                        const n = input ? parseInt(input.value) || 5 : 5;
                                                        const topN = Math.min(Math.max(2, n), topContributingDimensions.length);
                                                        const topNDimensions = topContributingDimensions
                                                            .slice(0, topN)
                                                            .map(v => v.dimension);
                                                        onPcaSelectedDimensionsChange(topNDimensions);
                                                    }}
                                                    className="text-xs text-blue-400 hover:text-blue-300 px-2 py-1 rounded bg-blue-800/30 hover:bg-blue-700/30"
                                                >
                                                    Use Top N
                                                </button>
                                            </div>
                                            <div className="max-h-32 overflow-y-auto space-y-1">
                                                {topContributingDimensions.slice(0, 10).map((vector) => {
                                                    const isSelected = pcaSelectedDimensions.some(d => d.id === vector.dimension.id);
                                                    return (
                                                        <div
                                                            key={vector.dimension.id}
                                                            className={`flex items-center justify-between text-xs p-1 rounded ${
                                                                isSelected ? 'bg-blue-900/30' : 'hover:bg-gray-600/50'
                                                            }`}
                                                        >
                                                            <div className="flex items-center gap-2 flex-1">
                                                                <span className="text-gray-400 w-6">#{vector.rank}</span>
                                                                <span className="text-white flex-1">{vector.dimension.name}</span>
                                                                <span className="text-gray-400">
                                                                    {vector.contributionPercent.toFixed(1)}%
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-xs text-gray-400 mt-2">
                                                Dimensions sorted by contribution magnitude. Enter N and click "Use Top N" to select.
                                            </p>
                                        </div>
                                    )}
                                    
                                    <label className="block text-sm font-medium text-gray-300">
                                        Dimensions for PCA ({pcaSelectedDimensions.length} selected)
                                    </label>
                                    <div className="max-h-48 overflow-y-auto border border-gray-600 rounded-lg p-2 bg-gray-700/50">
                                        {availableDimensions.length === 0 ? (
                                            <p className="text-xs text-gray-400">No dimensions available</p>
                                        ) : (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-600">
                                                    <button
                                                        onClick={() => {
                                                            if (pcaSelectedDimensions.length === availableDimensions.length) {
                                                                onPcaSelectedDimensionsChange([]);
                                                            } else {
                                                                onPcaSelectedDimensionsChange([...availableDimensions]);
                                                            }
                                                        }}
                                                        className="text-xs text-blue-400 hover:text-blue-300 px-2 py-1 rounded bg-blue-800/30 hover:bg-blue-700/30"
                                                    >
                                                        {pcaSelectedDimensions.length === availableDimensions.length ? 'Deselect All' : 'Select All'}
                                                    </button>
                                                </div>
                                                {availableDimensions.map((dimension) => {
                                                    const isSelected = pcaSelectedDimensions.some(d => d.id === dimension.id);
                                                    return (
                                                        <label
                                                            key={dimension.id}
                                                            className="flex items-center gap-2 cursor-pointer hover:bg-gray-600/50 p-1 rounded"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        onPcaSelectedDimensionsChange([...pcaSelectedDimensions, dimension]);
                                                                    } else {
                                                                        onPcaSelectedDimensionsChange(
                                                                            pcaSelectedDimensions.filter(d => d.id !== dimension.id)
                                                                        );
                                                                    }
                                                                }}
                                                                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                                                            />
                                                            <div className="flex-1">
                                                                <div className="text-sm text-white">{dimension.name}</div>
                                                                {dimension.description && (
                                                                    <div className="text-xs text-gray-400">{dimension.description}</div>
                                                                )}
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                    {pcaSelectedDimensions.length < 2 && availableDimensions.length >= 2 && (
                                        <div className="p-2 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
                                            <p className="text-xs text-yellow-300">
                                                Select at least 2 dimensions for PCA
                                            </p>
                                        </div>
                                    )}
                                    {availableDimensions.length < 2 && (
                                        <div className="p-2 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
                                            <p className="text-xs text-yellow-300">
                                                Need at least 2 dimensions available for PCA
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                </>
            )}

            {/* Tools Tab Content */}
            {activeTab === 'tools' && (
                <>
                    {/* Comparison Mode */}
                    <div className="p-3 border-b border-gray-700">
                        <h3 className="text-md font-medium text-white mb-3">Point Comparison</h3>
                        
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <label className="text-sm text-gray-300 flex items-center gap-2">
                                    <IconTriangle size={18} />
                                    Comparison Mode
                                </label>
                                <button
                                    onClick={() => {
                                        onComparisonModeChange(!isComparisonMode);
                                        if (isComparisonMode) {
                                            // Clear selection when disabling
                                            onSelectedPointsChange([null, null]);
                                        }
                                    }}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                        isComparisonMode ? 'bg-blue-600' : 'bg-gray-600'
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            isComparisonMode ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                    />
                                </button>
                            </div>
                            <p className="text-xs text-gray-400">
                                {isComparisonMode 
                                    ? 'Click two points on the canvas to compare them'
                                    : 'Enable to select and compare two points'}
                            </p>

                            {isComparisonMode && (selectedPoints[0] || selectedPoints[1]) && (
                                <button
                                    onClick={() => onSelectedPointsChange([null, null])}
                                    className="w-full px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                                >
                                    Clear Selection
                                </button>
                            )}

                            {/* Comparison Results */}
                            {comparisonResult && selectedPoints[0] && selectedPoints[1] && (
                                <div className="mt-4 p-3 bg-gray-700/50 rounded-lg border border-gray-600">
                                    <h4 className="text-sm font-medium text-white mb-3">Comparison Results</h4>
                                    
                                    {/* Selected Points Info */}
                                    <div className="mb-3 space-y-2">
                                        <div className="text-xs text-gray-300">
                                            <span className="font-medium">Point 1:</span> {getPointName(comparisonResult.point1)}
                                        </div>
                                        <div className="text-xs text-gray-300">
                                            <span className="font-medium">Point 2:</span> {getPointName(comparisonResult.point2)}
                                        </div>
                                    </div>

                                    {/* Coordinate Differences */}
                                    <div className="mb-3 p-2 bg-gray-800/50 rounded">
                                        <div className="text-xs font-medium text-gray-300 mb-1">Coordinate Difference</div>
                                        <div className="text-xs text-gray-400 space-y-1">
                                            <div>ΔX: {comparisonResult.coordinateDiff.x.toFixed(3)}</div>
                                            <div>ΔY: {comparisonResult.coordinateDiff.y.toFixed(3)}</div>
                                            <div>Distance: {comparisonResult.coordinateDiff.distance.toFixed(3)}</div>
                                        </div>
                                    </div>

                                    {/* Dimension Contributions */}
                                    {comparisonResult.dimensionDifferences.length > 0 && (
                                        <div className="mb-3">
                                            <div className="text-xs font-medium text-gray-300 mb-2">Dimension Contributions</div>
                                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                                {comparisonResult.dimensionDifferences.map((diff, idx) => {
                                                    const isPositive = diff.difference > 0;
                                                    const absDiff = Math.abs(diff.difference);
                                                    const maxAbsDiff = Math.max(...comparisonResult.dimensionDifferences.map(d => Math.abs(d.difference)));
                                                    const barWidth = (absDiff / maxAbsDiff) * 100;
                                                    
                                                    const isHovered = hoveredDimensionId === diff.dimension.id;
                                                    
                                                    return (
                                                        <div 
                                                            key={diff.dimension.id} 
                                                            className="space-y-1"
                                                            onMouseEnter={() => onHoveredDimensionChange(diff.dimension.id)}
                                                            onMouseLeave={() => onHoveredDimensionChange(null)}
                                                        >
                                                            {/* Dimension name as header */}
                                                            <div className={`text-xs font-medium truncate transition-colors ${
                                                                isHovered ? 'text-blue-400' : 'text-white'
                                                            }`} title={diff.dimension.name}>
                                                                {diff.dimension.name}
                                                            </div>
                                                            {/* Bar with numbers inline */}
                                                            <div className={`relative w-full bg-gray-600 rounded-full h-4 overflow-hidden transition-all ${
                                                                isHovered ? 'ring-2 ring-blue-500' : ''
                                                            }`}>
                                                                <div
                                                                    className={`h-full transition-all flex items-center ${
                                                                        isPositive ? 'bg-green-500' : 'bg-red-500'
                                                                    }`}
                                                                    style={{ width: `${barWidth}%` }}
                                                                >
                                                                    {barWidth > 20 && (
                                                                        <span className="text-[10px] font-medium text-white px-2 whitespace-nowrap">
                                                                            {isPositive ? '+' : ''}{diff.difference.toFixed(3)}
                                                                            {diff.contributionPercent !== undefined && ` (${diff.contributionPercent.toFixed(1)}%)`}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {barWidth <= 20 && (
                                                                    <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-medium ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                                                                        {isPositive ? '+' : ''}{diff.difference.toFixed(3)}
                                                                        {diff.contributionPercent !== undefined && ` (${diff.contributionPercent.toFixed(1)}%)`}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-gray-400">
                                                                {getPointName(comparisonResult.point1)}: {diff.value1.toFixed(3)} → {getPointName(comparisonResult.point2)}: {diff.value2.toFixed(3)}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Textual Descriptions */}
                                    {comparisonResult.dimensionDifferences.length > 0 && (
                                        <div className="mt-3 p-2 bg-gray-800/50 rounded">
                                            <div className="text-xs font-medium text-gray-300 mb-2">Differences</div>
                                            <div className="space-y-1 text-xs text-gray-400">
                                                {generateDifferenceDescriptions(comparisonResult).map((desc, idx) => (
                                                    <div key={idx} className="py-1 border-b border-gray-700 last:border-0">
                                                        {desc}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {comparisonResult.dimensionDifferences.length === 0 && (
                                        <div className="text-xs text-gray-400 text-center py-2">
                                            No dimension differences found (points are identical in all dimensions)
                                        </div>
                                    )}
                                </div>
                            )}

                            {isComparisonMode && (!selectedPoints[0] || !selectedPoints[1]) && (
                                <div className="mt-3 p-2 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                                    <p className="text-xs text-blue-300">
                                        {!selectedPoints[0] && !selectedPoints[1] 
                                            ? 'Click on the first point to select it'
                                            : 'Click on the second point to complete the comparison'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Vector Field */}
                    <div className="p-3 border-b border-gray-700">
                        <h3 className="text-md font-medium text-white mb-3">Query→Match Vector Field</h3>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <label className="text-sm text-gray-300 flex items-center gap-2">
                                    <IconChartLine size={18} />
                                    Show Vector Field
                                </label>
                                <button
                                    onClick={() => onShowVectorFieldChange(!showVectorField)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                        showVectorField ? 'bg-blue-600' : 'bg-gray-600'
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            showVectorField ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                    />
                                </button>
                            </div>
                            <p className="text-xs text-gray-400">
                                Draw a fixed-length arrow from each query toward its positive matches
                                (perfect match, good match, match). Reveals global shifts between queries and entities.
                            </p>
                            {showVectorField && (
                                <div className="mt-2">
                                    <label className="block text-xs font-medium text-gray-400 mb-1">Arrow Mode</label>
                                    <div className="flex rounded-lg overflow-hidden border border-gray-600">
                                        <button
                                            onClick={() => onVectorFieldModeChange('individual')}
                                            className={`flex-1 px-2 py-1.5 text-xs transition-colors ${
                                                vectorFieldMode === 'individual'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-700 text-gray-400 hover:text-white'
                                            }`}
                                        >
                                            Individual
                                        </button>
                                        <button
                                            onClick={() => onVectorFieldModeChange('average')}
                                            className={`flex-1 px-2 py-1.5 text-xs transition-colors ${
                                                vectorFieldMode === 'average'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-700 text-gray-400 hover:text-white'
                                            }`}
                                        >
                                            Average
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {vectorFieldMode === 'individual'
                                            ? 'One arrow per matched entity'
                                            : 'Single arrow toward match centroid'}
                                    </p>

                                    <label className="block text-xs font-medium text-gray-400 mb-1 mt-3">Arrow Length</label>
                                    <div className="flex rounded-lg overflow-hidden border border-gray-600">
                                        <button
                                            onClick={() => onVectorFieldLengthChange('field')}
                                            className={`flex-1 px-2 py-1.5 text-xs transition-colors ${
                                                vectorFieldLength === 'field'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-700 text-gray-400 hover:text-white'
                                            }`}
                                        >
                                            Fixed
                                        </button>
                                        <button
                                            onClick={() => onVectorFieldLengthChange('full')}
                                            className={`flex-1 px-2 py-1.5 text-xs transition-colors ${
                                                vectorFieldLength === 'full'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-700 text-gray-400 hover:text-white'
                                            }`}
                                        >
                                            Full
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {vectorFieldLength === 'field'
                                            ? 'Fixed-length arrows showing direction only'
                                            : 'Full-length arrows reaching the matched entity'}
                                    </p>

                                    <div className="flex items-center justify-between mt-3">
                                        <label className="text-xs font-medium text-gray-400">Shimmer</label>
                                        <button
                                            onClick={() => onVectorFieldShimmerChange(!vectorFieldShimmer)}
                                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                                vectorFieldShimmer ? 'bg-blue-600' : 'bg-gray-600'
                                            }`}
                                        >
                                            <span
                                                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                                    vectorFieldShimmer ? 'translate-x-4' : 'translate-x-0.5'
                                                }`}
                                            />
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Animate through queries one by one, ordered by spatial proximity.
                                    </p>
                                </div>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                                Query matches are fetched on demand when enabled.
                            </p>
                        </div>
                    </div>
                </>
            )}

            {/* Data Summary - Always visible at bottom */}
            <div className="p-3 flex-shrink-0 mt-auto border-t border-gray-700">
                <h3 className="text-md font-medium text-white mb-3">Data Summary</h3>
                
                <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                        <span className="text-gray-400">Entities:</span>
                        <span className="text-white">
                            {entitiesLoading ? "..." : "Loaded"}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-400">Queries:</span>
                        <span className="text-white">
                            {queriesLoading ? "..." : "Loaded"}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-400">Dimensions:</span>
                        <span className="text-white">
                            {dimensionsLoading ? "..." : `${availableDimensions.length} available`}
                        </span>
                    </div>
                    {dimensionCollection && (
                        <div className="flex justify-between">
                            <span className="text-gray-400">Live Collection:</span>
                            <span className="text-green-400">Active</span>
                        </div>
                    )}
                </div>
            </div>
                </>
            )}
            
            {/* Collapsed view - just icons */}
            {isCollapsed && (
                <div className="flex flex-col items-center py-4 space-y-4">
                    {activeTab === 'search' && <IconSearch size={20} className="text-gray-400" />}
                    {activeTab === 'dimensionality' && <IconChartBar size={20} className="text-gray-400" />}
                    {activeTab === 'analytics' && <IconChartLine size={20} className="text-gray-400" />}
                    {activeTab === 'tools' && <IconTools size={20} className="text-gray-400" />}
                </div>
            )}
            </div>
        </div>
    );
};

export default AnalysisSidebar;
